import { VehicleType } from '@thive/fddm-types';

import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { IHistoryService } from '@/modules/canvas/services/history/IHistoryService';
import { FpsServiceFactory } from '@/modules/floorplanV2/service/FpsServiceFactory';
import { Step } from '@/modules/floorplanV2/types';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import {
  FileReferenceResponse,
  UnifiedVehicle,
  UnifiedVehicleDetails,
  ValidAssetType,
} from '@/modules/vehicles/types';

import { IVehicleService } from './IVehicleService';

export class VehicleServiceHistoryDecorator implements IVehicleService {
  private readonly decoree: IVehicleService;
  private readonly historySvc: IHistoryService;

  public constructor(decoree: IVehicleService) {
    this.decoree = decoree;
    this.historySvc = HistoryServiceFactory.getInstance().createHistoryService();
  }

  public async loadVehicles(): Promise<void> {
    return this.decoree.loadVehicles();
  }

  public getVehicle(vehicleId: string): UnifiedVehicle {
    return this.decoree.getVehicle(vehicleId);
  }

  public async getVehicleJSONAsset(reference: string): Promise<unknown | null> {
    return this.decoree.getVehicleJSONAsset(reference);
  }

  public async fetchAndUploadAssetToFileApi(
    vehicleId: string,
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    assetType: ValidAssetType,
  ): Promise<FileReferenceResponse | null> {
    return this.decoree.fetchAndUploadAssetToFileApi(
      vehicleId,
      projectId,
      layoutAlternativeId,
      revisionId,
      assetType,
    );
  }

  public async ensureVehicleAssetAvailability(
    vehicleIds: string[],
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    floorplanVehicleTypes?: VehicleType[],
  ): Promise<VehicleType[]> {
    const previousSignature = this.getVehicleTypesSignature();
    const result = await this.historySvc.batchAsync(() =>
      this.decoree.ensureVehicleAssetAvailability(
        vehicleIds,
        projectId,
        layoutAlternativeId,
        revisionId,
        floorplanVehicleTypes,
      ),
    );

    this.invalidateFloorplanIfChanged(previousSignature);

    return result;
  }

  public async ensureVehicleDetailsAvailability(
    vehicleIds: string[],
    floorplanVehicleTypes?: VehicleType[],
  ): Promise<void> {
    return this.decoree.ensureVehicleDetailsAvailability(vehicleIds, floorplanVehicleTypes);
  }

  public async reconcileEnabledVehicleTypes(
    vehicleIds: string[],
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    addNewVehiclesToHighways = false,
  ): Promise<VehicleType[]> {
    const previousSignature = this.getVehicleTypesSignature();
    const result = await this.historySvc.batchAsync(() =>
      this.decoree.reconcileEnabledVehicleTypes(
        vehicleIds,
        projectId,
        layoutAlternativeId,
        revisionId,
        addNewVehiclesToHighways,
      ),
    );

    this.invalidateFloorplanIfChanged(previousSignature);

    return result;
  }

  public cleanUpRedundantTypes(): void {
    const previousSignature = this.getVehicleTypesSignature();
    this.historySvc.batch(() => {
      this.decoree.cleanUpRedundantTypes();
    });

    this.invalidateFloorplanIfChanged(previousSignature);
  }

  public setVehicleDetails(vehicleDetails: UnifiedVehicleDetails[]): void {
    this.decoree.setVehicleDetails(vehicleDetails);
  }

  public removeVehicleDetails(vehicleIds: string[]): void {
    this.decoree.removeVehicleDetails(vehicleIds);
  }

  public async getVehicleTypeFiles(vehicleTypes: VehicleType[]): Promise<{
    uvtFiles: object[];
    uvtFileNames: string[];
    kmMdbFiles: Blob[];
    kmMdbFileNames: string[];
  }> {
    return this.decoree.getVehicleTypeFiles(vehicleTypes);
  }

  public async getLatestFileRef(
    assetType: ValidAssetType,
    projectId: string,
    layoutAlternativeId: string,
    revisionId: string,
    vehicleId: string,
  ): Promise<void> {
    const previousSignature = this.getVehicleTypesSignature();
    await this.historySvc.batchAsync(() =>
      this.decoree.getLatestFileRef(
        assetType,
        projectId,
        layoutAlternativeId,
        revisionId,
        vehicleId,
      ),
    );
    this.invalidateFloorplanIfChanged(previousSignature);
  }

  private getVehicleTypesSignature(): string {
    return JSON.stringify(useFloorplanDesignStore.getState().getVehicleTypes());
  }

  private invalidateFloorplanIfChanged(previousSignature: string): void {
    if (previousSignature === this.getVehicleTypesSignature()) {
      return;
    }

    FpsServiceFactory.getInstance().createFpsService().setStep(Step.Validate);
  }
}
